import { memo, useEffect, useState, useMemo } from 'react';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { styled } from '@mui/material';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

const BINARY_FORMATS = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'bmp'];

const DocViewerWrapper = styled('div')({
  width: '100%',
  height: '100%',
  minHeight: 0,
  overflow: 'hidden',
  overscrollBehavior: 'contain',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'transparent',

  '& #react-doc-viewer': {
    height: '100% !important',
    minHeight: '0 !important',
    flex: 1,
    backgroundColor: 'transparent !important',
  },

  '& #proxy-renderer': {
    height: '100% !important',
    minHeight: '0 !important',
    backgroundColor: 'transparent !important',
  },

  '& #pdf-renderer, & #html-renderer': {
    height: '100% !important',
    minHeight: '0 !important',
    flex: 1,
    backgroundColor: 'transparent !important',
  },

  '& #pdf-renderer': {
    overflow: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255, 255, 255, 0.55) rgba(255, 255, 255, 0.12)',
    '&::-webkit-scrollbar': {
      width: '10px',
      height: '10px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
      borderRadius: '5px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(255, 255, 255, 0.55)',
      borderRadius: '5px',
      border: '2px solid transparent',
      backgroundClip: 'content-box',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.75)',
    },
  },

  '& #html-renderer': {
    padding: '0 !important',
    boxSizing: 'border-box',
  },

  '& #html-body': {
    height: '100% !important',
    width: '100% !important',
    margin: '0 !important',
    padding: '0 !important',
    border: 'none !important',
    backgroundColor: '#fff',
    display: 'block',
  },

  '& #pdf-controls': {
    position: 'fixed',
    top: '10px',
    right: '52px',
    left: 'auto',
    zIndex: 1400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '4px',
    padding: '0',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    pointerEvents: 'auto',
  },

  '& #pdf-controls > button, & #pdf-controls > a': {
    width: '40px',
    height: '40px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '8px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    border: 'none',
    padding: 0,
    color: '#ffffff !important',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
    '&:disabled': {
      opacity: 0.35,
      cursor: 'default',
    },
  },

  '& #pdf-controls > button svg, & #pdf-controls > a svg': {
    width: '20px !important',
    height: '20px !important',
    display: 'block',
    color: '#ffffff !important',
  },

  // Only paint drawable shapes — overriding <g fill="none"> turns arrows into squares
  '& #pdf-controls > button svg path, & #pdf-controls > a svg path, & #pdf-controls > button svg polygon, & #pdf-controls > a svg polygon': {
    fill: '#ffffff !important',
  },

  // Reset / expand icon fills more of its viewBox — match optical size
  '& #pdf-zoom-reset svg, & #pdf-toggle-pagination svg': {
    width: '12px !important',
    height: '12px !important',
  },

  '& #pdf-pagination': {
    position: 'fixed',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1400,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 14px',
    borderRadius: '24px',
    backgroundColor: '#2a2a2a',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.4)',
  },

  '& #pdf-pagination button': {
    width: '32px',
    height: '32px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '6px',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
    '&:disabled': {
      opacity: 0.35,
      cursor: 'default',
    },
  },

  '& #pdf-pagination button svg': {
    width: '14px !important',
    height: '14px !important',
    display: 'block',
  },

  '& #pdf-pagination button svg path, & #pdf-pagination button svg polygon': {
    fill: '#ffffff !important',
  },

  '& #pdf-pagination-info': {
    color: '#fff !important',
    fontSize: '13px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    margin: '0 4px',
  },

  '& #pdf-page-info, & .react-pdf__Page__textContent, & .textLayer, & .react-pdf__Page__annotations, & .annotationLayer': {
    display: 'none !important',
  },

  '& #pdf-page-wrapper': {
    margin: '0 auto !important',
    display: 'flex',
    justifyContent: 'center',
  },

  '& .react-pdf__Page__canvas': {
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.45)',
  },

  '& img': {
    maxWidth: '100%',
    maxHeight: 'calc(100vh - 120px)',
    objectFit: 'contain',
    margin: 'auto',
    display: 'block',
  },

  '& #no-renderer': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    gap: '16px',
    padding: '24px',
    boxSizing: 'border-box',
    color: 'rgba(255, 255, 255, 0.85)',
    fontSize: '15px',
    fontWeight: 400,
    textAlign: 'center',
  },

  '& #no-renderer-download': {
    width: 'auto !important',
    minWidth: '140px',
    height: '36px !important',
    padding: '0 20px',
    backgroundColor: '#2a2a2a !important',
    color: '#ffffff !important',
    borderRadius: '24px',
    boxShadow: 'none',
    fontSize: '14px',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: '#3a3a3a !important',
    },
  },
});

interface Props {
  fileBlob: Blob | string;
  fileName: string;
  fileType: string;
}

const DocPreviewBase = ({ fileBlob, fileName, fileType }: Props) => {
  const ext = fileType?.toLowerCase() ?? '';
  const [documents, setDocuments] = useState<Array<{ uri: string; fileName: string; fileType: string }>>([]);

  const activeRenderers = useMemo(() => {
    return BINARY_FORMATS.includes(ext) ? DocViewerRenderers.filter((renderer) => !renderer.fileTypes?.includes('txt')) : DocViewerRenderers;
  }, [ext]);

  useEffect(() => {
    const url = typeof fileBlob === 'string' ? fileBlob : URL.createObjectURL(fileBlob);

    setDocuments([{ uri: url, fileName, fileType: ext }]);

    return () => {
      if (typeof fileBlob !== 'string') {
        URL.revokeObjectURL(url);
      }
    };
  }, [fileBlob, fileName, ext]);

  if (!documents.length) return null;

  return (
    <DocViewerWrapper>
      <DocViewer
        documents={documents}
        pluginRenderers={activeRenderers}
        theme={{
          primary: '#ffffff',
          secondary: '#121212',
          tertiary: 'transparent',
          textPrimary: '#ffffff',
          textSecondary: '#ffffff',
          textTertiary: 'rgba(255, 255, 255, 0.7)',
          disableThemeScrollbar: true,
        }}
        config={{
          header: {
            disableHeader: true,
            disableFileName: true,
            retainURLParams: true,
          },
          pdfVerticalScrollByDefault: true,
        }}
      />
    </DocViewerWrapper>
  );
};

export const DocPreview = memo(DocPreviewBase) as typeof DocPreviewBase;
